import z from "zod";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import { sureApiCall } from "../axiosClient/sureApi";
import { zodParseWithLog } from "../apiErrorLogs";
import { fetchAllHydraMembers } from "../_common/hydra/fetchAllHydraMembers";

/**
 * Hook for the pre-aggregated PACK_AGB packaging formats exposed by sure-api
 * (`PackagingReferenceComposite`). Mirrors {@link useConsumptionReference}.
 *
 * Used by the "Create composite from sure ref" form (mode A): selecting an
 * item here freezes the composite to its sure-side pre-computed scores.
 *
 * Localisation contract: sure-api uses Gedmo Translatable on
 * `PackagingReferenceComposite.name`. The axios client injects
 * `Accept-Language` from `i18next.language`, so the API already returns
 * `name` localised.
 */
export const PackagingReferenceCompositeApiSchema = z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    defaultWeight: z.number().nullable().optional(),
    ecobalyseProcessUuid: z.string().nullable().optional(),
    components: z
        .array(
            z.object({
                packagingReference: z.object({
                    id: z.number(),
                    name: z.string(),
                    defaultWeight: z.number().nullable().optional(),
                }),
                amount: z.number(),
                position: z.number(),
                isMain: z.boolean().optional(),
            }),
        )
        .optional(),
});

export type PackagingReferenceCompositeApi = z.infer<typeof PackagingReferenceCompositeApiSchema>;

export type PackagingReferenceComposite = {
    id: number;
    code: string;
    label: string;
    defaultWeight: number | null;
    ecobalyseProcessUuid: string | null;
    components: Array<{
        packagingReferenceId: number;
        label: string;
        defaultWeight: number | null;
        amount: number;
        position: number;
        isMain: boolean;
    }>;
};

/**
 * Mirrors {@link projectPackagingReference} for the composite catalogue —
 * exported so unit tests can exercise the empty/whitespace fallback rule
 * without spinning up React Query.
 */
export const projectPackagingReferenceComposite = (
    item: PackagingReferenceCompositeApi,
): PackagingReferenceComposite => ({
    id: item.id,
    code: item.code,
    label: item.name.trim() !== "" ? item.name : item.code,
    defaultWeight: item.defaultWeight ?? null,
    ecobalyseProcessUuid: item.ecobalyseProcessUuid ?? null,
    components: (item.components ?? []).map((component) => ({
        packagingReferenceId: component.packagingReference.id,
        label: component.packagingReference.name,
        defaultWeight: component.packagingReference.defaultWeight ?? null,
        amount: component.amount,
        position: component.position,
        isMain: component.isMain ?? false,
    })),
});

export const usePackagingReferenceComposite = () => {
    const { i18n } = useTranslation();
    const lang = i18n.language?.startsWith("fr") ? "fr" : "en";

    const query = useQuery({
        cacheTime: 0,
        staleTime: 0,
        queryKey: ["allPackagingReferenceComposites", lang],
        queryFn: async () => {
            return await fetchAllHydraMembers({
                client: sureApiCall,
                endpoint: "/api/packaging_reference_composites",
                itemsPerPage: 100,
                requestConfig: { headers: { "Accept-Language": lang } },
            });
        },
        select: (items) => {
            const data = zodParseWithLog(
                items,
                PackagingReferenceCompositeApiSchema.array(),
                "allPackagingReferenceComposites",
            );

            return data.map(projectPackagingReferenceComposite);
        },
    });

    return {
        packagingReferenceComposites: query.data ?? [],
        ...query,
    };
};
